"use client";

import React, { useMemo, useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import {
  Package,
  Tag,
  Tags,
  X,
  LucideIcon,
  User,
  Users,
  ContactRound,
  MessageSquare,
  MessageSquareText,
  MessageSquareWarning,
  Mails,
  LayoutTemplate,
  Coins,
  ShoppingCart,
  TriangleAlert,
  Banknote,
  BanknoteArrowDown,
  Truck,
  DatabaseBackup,
  RefreshCw,
  Bell,
  ChevronDown,
  ChevronRight,
  Settings,
  Database,
  CreditCard,
  Landmark,
  Unlock,
  PackageSearch,
  CalendarClock,
  FileSpreadsheet,
  Warehouse,
  MapPin,
  Ruler,
  Plug,
  Handshake,
  BookOpen,
  Eye,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { CustomTooltip } from "./tooltip";
import { useCan } from "@/lib/hooks/useCan";
import type { PermissionString } from "@/lib/auth/ability";

export interface NavSubRoute {
  name: string;
  path: string;
  Icon: LucideIcon;
  label: string;
  permission: PermissionString;
}

export interface NavRoute {
  name: string;
  path?: string;
  Icon: LucideIcon;
  label: string;
  subRoutes?: NavSubRoute[];
}

interface AdminMenuProps {
  isOpen: boolean;
  onClose: () => void;
  fullScreen?: boolean;
  defaultRoutes?: NavRoute[];
}

// --- Configuración de Rutas (Open-Closed Principle) ---
export const ADMIN_ROUTES: NavRoute[] = [
  {
    name: "Productos",
    Icon: Package,
    label: "Productos",
    subRoutes: [
      {
        name: "Productos",
        path: "/admin/productos",
        permission: "AdminProduct.access",
        Icon: Package,
        label: "Productos",
      },
      {
        name: "Marcas",
        path: "/admin/marcas",
        permission: "AdminBrands.access",
        Icon: Tags,
        label: "Marcas",
      },
      {
        name: "Simulador de precios",
        path: "/admin/simulador-precios",
        permission: "AdminPricing.access",
        Icon: Coins,
        label: "Simulador de precios",
      },
      {
        name: "Listas de precios",
        path: "/admin/listas-precios",
        permission: "AdminPriceLists.access",
        Icon: FileSpreadsheet,
        label: "Listas de precios",
      },
      {
        name: "Categorías",
        path: "/admin/categorias",
        permission: "AdminEngineering.access",
        Icon: Tag,
        label: "Categorías",
      },
      {
        name: "Stock reservado",
        path: "/admin/stock-reservado",
        permission: "AdminStockReservations.access",
        Icon: PackageSearch,
        label: "Stock reservado",
      },
    ],
  },
  {
    name: "Almacén",
    Icon: Warehouse,
    label: "Almacén",
    subRoutes: [
      {
        name: "Marcas volumen",
        path: "/admin/almacen/marcas-volumen",
        permission: "AdminShippingConfig.access",
        Icon: Truck,
        label: "Marcas volumen",
      },
      {
        name: "Códigos postales de León",
        path: "/admin/almacen/codigos-postales-leon",
        permission: "AdminShippingConfig.access",
        Icon: MapPin,
        label: "Códigos postales de León",
      },
      {
        name: "Artículos con dimensiones extras",
        path: "/admin/almacen/articulos-dimensiones-extras",
        permission: "AdminShippingConfig.access",
        Icon: Ruler,
        label: "Artículos con dimensiones extras",
      },
    ],
  },

  {
    name: "Pedidos",
    Icon: ShoppingCart,
    label: "Pedidos",
    subRoutes: [
      {
        name: "Gestión de pedidos",
        path: "/admin/pedidos",
        permission: "AdminPurchaseOrders.access",
        Icon: ShoppingCart,
        label: "Pedidos",
      },
      {
        name: "Errores en pedidos",
        path: "/admin/pedidos/errores",
        permission: "AdminSalesIssues.access",
        Icon: TriangleAlert,
        label: "Errores en pedidos",
      },
    ],
  },
  {
    name: "Usuarios",
    Icon: Users,
    label: "Usuarios",
    subRoutes: [
      {
        name: "Usuarios",
        path: "/admin/usuarios",
        permission: "AdminBackoffice.access",
        Icon: User,
        label: "Usuarios",
      },
      {
        name: "Clientes",
        path: "/admin/clientes",
        permission: "AdminBackoffice.access",
        Icon: ContactRound,
        label: "Clientes",
      },
      {
        name: "Solicitudes integrador",
        path: "/admin/solicitudes-integrador",
        permission: "AdminIntegratorRequests.access",
        Icon: MessageSquareWarning,
        label: "Solicitudes integrador",
      },
      {
        name: "Solicitudes de proyecto",
        path: "/admin/solicitudes-contacto",
        permission: "AdminSales.access",
        Icon: MessageSquare,
        label: "Solicitudes de proyecto",
      },
      {
        name: "Comentarios de servicio",
        path: "/admin/comentarios-servicio",
        permission: "AdminSales.access",
        Icon: MessageSquareText,
        label: "Comentarios de servicio",
      },
      {
        name: "Proveedores",
        path: "/admin/proveedores",
        permission: "AdminBackoffice.access",
        Icon: Truck,
        label: "Proveedores",
      },
      {
        name: "Desbloquear usuarios",
        path: "/admin/desbloquear-usuarios",
        permission: "AdminUserUnlock.access",
        Icon: Unlock,
        label: "Desbloquear usuarios",
      },
    ],
  },
  {
    name: "Finanzas",
    Icon: Coins,
    label: "Finanzas",
    subRoutes: [
      {
        name: "Transferencias bancarias",
        path: "/admin/comprobantes-pago",
        permission: "AdminFinance.access",
        Icon: BanknoteArrowDown,
        label: "Transferencias bancarias",
      },
      {
        name: "Pagos con tarjeta",
        path: "/admin/pagos-tarjeta",
        permission: "AdminFinance.access",
        Icon: CreditCard,
        label: "Pagos con tarjeta",
      },
      {
        name: "Conciliación de pagos con tarjeta",
        path: "/admin/santander-consulta",
        permission: "AdminFinance.access",
        Icon: Landmark,
        label: "Conciliación de pagos con tarjeta",
      },
      {
        name: "Mantenimiento santander",
        path: "/admin/santander-mantenimiento",
        permission: "AdminSantanderMaintenance.access",
        Icon: CalendarClock,
        label: "Ventanas de mantenimiento santander",
      },
      {
        name: "Reembolsos",
        path: "/admin/reembolsos",
        permission: "AdminFinance.access",
        Icon: Banknote,
        label: "Reembolsos de ordenes canceladas",
      },
    ],
  },
  {
    name: "Notificaciones",
    Icon: Bell,
    label: "Notificaciones",
    subRoutes: [
      {
        name: "Configurar correo",
        path: "/admin/configurar-correo",
        permission: "AdminSystem.access",
        Icon: Mails,
        label: "configuraciones de correo",
      },

      {
        name: "Templates de correo",
        path: "/admin/plantillas-correo",
        permission: "AdminMarketing.access",
        Icon: LayoutTemplate,
        label: "Templates de correo",
      },

      {
        name: "Gestión de notificaciones",
        path: "/admin/gestion-notificaciones",
        permission: "AdminMarketing.access",
        Icon: Settings,
        label: "Gestión de notificaciones",
      },
    ],
  },
  {
    name: "Gestión de sincronización",
    Icon: RefreshCw,
    label: "Gestión de sincronizaciones",
    subRoutes: [
      {
        name: "Reindexación",
        path: "/admin/reindexacion",
        permission: "AdminSystem.access",
        Icon: RefreshCw,
        label: "Página de reindexación",
      },
      {
        name: "Sincronización",
        path: "/admin/gestion-sincronizacion",
        permission: "AdminSync.access",
        Icon: DatabaseBackup,
        label: "Gestión de los procesos de sincronización",
      },
    ],
  },
  {
    name: "API de terceros",
    Icon: Plug,
    label: "API de terceros",
    subRoutes: [
      {
        name: "Clientes de APIs",
        path: "/admin/api-terceros/clientes",
        permission: "AdminPartnerApi.access",
        Icon: Handshake,
        label: "Clientes de APIs",
      },
      {
        name: "Pedidos de terceros",
        path: "/admin/api-terceros/pedidos",
        permission: "AdminPartnerApi.access",
        Icon: PackageSearch,
        label: "Pedidos de terceros",
      },
      {
        name: "Instantánea del catálogo",
        path: "/admin/api-terceros/catalogo",
        permission: "AdminPartnerApi.access",
        Icon: BookOpen,
        label: "Instantánea del catálogo",
      },
      {
        name: "Catálogo de terceros",
        path: "/admin/api-terceros/lo-que-ve",
        permission: "AdminPartnerApi.access",
        Icon: Eye,
        label: "Catálogo de terceros",
      },
    ],
  },
  {
    name: "Páginas estáticas",
    Icon: LayoutTemplate,
    label: "Páginas estáticas",
    subRoutes: [
      {
        name: "Páginas estáticas",
        path: "/admin/gestor-paginas",
        permission: "AdminMarketing.access",
        Icon: LayoutTemplate,
        label: "Páginas estáticas",
      },
      {
        name: "Invalidar caché",
        path: "/admin/invalidacion-cache",
        permission: "AdminCache.access",
        Icon: Database,
        label: "Invalidar caché",
      },
    ],
  },
];

export function AdminMenu({
  isOpen,
  onClose,
  fullScreen = false,
  defaultRoutes = ADMIN_ROUTES,
}: AdminMenuProps) {
  const pathname = usePathname();
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>(
    () => {
      const initial: Record<string, boolean> = {};
      defaultRoutes.forEach((route) => {
        if (route.subRoutes) {
          const isSubrouteActive = route.subRoutes.some(
            (sub) =>
              pathname === sub.path || pathname.startsWith(`${sub.path}/`),
          );
          if (isSubrouteActive) {
            initial[route.name] = true;
          }
        }
      });
      return initial;
    },
  );

  useEffect(() => {
    defaultRoutes.forEach((route) => {
      if (route.subRoutes) {
        const isSubrouteActive = route.subRoutes.some(
          (sub) => pathname === sub.path || pathname.startsWith(`${sub.path}/`),
        );
        if (isSubrouteActive) {
          setExpandedMenus((prev) => {
            if (prev[route.name]) return prev;
            return {
              [route.name]: true,
            };
          });
        }
      }
    });
  }, [pathname, defaultRoutes]);

  const toggleMenu = (name: string) => {
    setExpandedMenus((prev) => {
      const isAlreadyExpanded = prev[name];
      if (isAlreadyExpanded) {
        return {};
      } else {
        return {
          [name]: true,
        };
      }
    });
  };

  const can = useCan();

  // Memorizamos las rutas para evitar cálculos innecesarios en re-renders, filtrando por rol
  const routes = useMemo(() => {
    return defaultRoutes
      .map((route) => ({
        ...route,
        subRoutes: route.subRoutes?.filter((subRoute) =>
          can(subRoute.permission),
        ),
      }))
      .filter((route) => !route.subRoutes || route.subRoutes.length > 0);
  }, [defaultRoutes, can]);

  return (
    <>
      {/* 1. Backdrop / Overlay (Mobile Only) */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 md:hidden",
          isOpen ? "opacity-100 visible" : "opacity-0 invisible",
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* 2. Main Sidebar Container */}
      <aside
        className={cn(
          `flex flex-col max-h-[calc(100vh-1rem)] min-h-[calc(100vh-1rem)] bg-white
          top-2 ${fullScreen ? "" : "mt-2"} transition-all duration-300 ease-in-out z-50 ${fullScreen ? "border-primary/20 border-r rounded-[16px]" : "rounded-[16px]"}`,
          "fixed md:sticky md:top-2 md:left-0",
          isOpen
            ? `${fullScreen ? "w-[260px]" : "w-[260px]"} translate-x-0`
            : "w-[70px] -translate-x-full md:translate-x-0",
        )}
      >
        {/* Header: Logos & Close Button */}
        {!fullScreen && (
          <div className="flex items-center justify-between p-3 h-[70px]">
            <Link
              href="/"
              prefetch={false}
              className=" w-full h-full flex items-center justify-center"
            >
              {/* Logo Full */}
              <Image
                src="/Logoazulhorizontal.png"
                alt="Adises Logo"
                width={140}
                height={40}
                priority
                className={cn(
                  "absolute transition-all duration-500",
                  isOpen
                    ? "opacity-100 scale-100"
                    : "opacity-0 scale-95 pointer-events-none",
                )}
              />
              {/* Logo Icono */}
              <Image
                src="/LogoAzulVerticalx.png"
                alt="Adises Icon"
                width={45}
                height={45}
                priority
                className={cn(
                  "absolute transition-all duration-500",
                  !isOpen
                    ? "opacity-100 scale-100"
                    : "opacity-0 scale-95 pointer-events-none",
                )}
              />
            </Link>

            {/* Botón de cierre (Solo visible en móvil + abierto) */}
            {isOpen && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-full md:hidden"
                aria-label="Cerrar menú"
              >
                <X size={20} />
              </button>
            )}
          </div>
        )}

        {/* Navigation Routes */}
        <nav className="flex flex-col pr-2 flex-1 overflow-y-auto custom-scrollbar">
          {routes.map((route, index) => {
            const subRoutes = route.subRoutes;
            if (subRoutes) {
              const isSubrouteActive = subRoutes.some(
                (sub) =>
                  pathname === sub.path || pathname.startsWith(`${sub.path}/`),
              );
              const isExpanded = expandedMenus[route.name];

              return (
                <React.Fragment key={route.name}>
                  {!isOpen ? (
                    <CustomTooltip content={route.name} side="right">
                      <Link
                        href={subRoutes[0].path}
                        prefetch={false}
                        onClick={() => {
                          if (window.innerWidth < 768) {
                            onClose();
                          }
                        }}
                        className={cn(
                          `p-1 cursor-pointer ${index === 0 ? "mt-0" : "mt-1"} w-full transition-all duration-200 gap-2 flex items-center pl-3 rounded-r-lg`,
                          isSubrouteActive
                            ? "font-bold shadow-sm bg-primary text-white"
                            : "hover:text-primary",
                        )}
                      >
                        <route.Icon
                          size={fullScreen ? 10 : 24}
                          className="shrink-0 mx-2"
                        />
                      </Link>
                    </CustomTooltip>
                  ) : (
                    <div className="flex flex-col w-full">
                      <button
                        onClick={() => toggleMenu(route.name)}
                        className={cn(
                          `p-2 cursor-pointer ${index === 0 ? "mt-0" : "mt-1"} w-full transition-all duration-200 gap-3 flex items-center justify-between !rounded-r-lg text-left hover:text-primary`,
                          isSubrouteActive
                            ? "text-primary font-bold"
                            : "text-slate-700",
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <route.Icon
                            size={fullScreen ? 15 : 24}
                            className={cn(
                              "shrink-0",
                              fullScreen && "!text-primary",
                            )}
                          />
                          <span
                            className={cn(
                              "truncate animate-in fade-in slide-in-from-left-2 duration-300",
                              fullScreen && "!text-primary",
                            )}
                          >
                            {route.name}
                          </span>
                        </div>
                        {isExpanded ? (
                          <ChevronDown
                            size={16}
                            className="shrink-0 text-slate-500"
                          />
                        ) : (
                          <ChevronRight
                            size={16}
                            className="shrink-0 text-slate-500"
                          />
                        )}
                      </button>
                      {isExpanded && (
                        <div className="pl-3 flex flex-col mt-0.5 space-y-0.5 border-l border-slate-100 ml-3">
                          {subRoutes.map((subRoute) => {
                            const isSubActive =
                              pathname === subRoute.path ||
                              (pathname.startsWith(`${subRoute.path}/`) &&
                                !subRoutes.some(
                                  (otherSub) =>
                                    otherSub.path !== subRoute.path &&
                                    pathname.startsWith(otherSub.path),
                                ));

                            return (
                              <Link
                                key={subRoute.path}
                                href={subRoute.path}
                                prefetch={false}
                                onClick={() => {
                                  if (window.innerWidth < 768) {
                                    onClose();
                                  }
                                }}
                                className={cn(
                                  "p-2 cursor-pointer w-full transition-all duration-200 gap-3 flex items-center !rounded-r-lg text-sm",
                                  isSubActive
                                    ? "font-bold shadow-sm bg-primary text-white"
                                    : "text-slate-600 hover:text-primary",
                                )}
                              >
                                <subRoute.Icon size={18} className="shrink-0" />
                                <span className="truncate">
                                  {subRoute.name}
                                </span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            }

            // Normal routes (without subRoutes)
            const isActive =
              route.path &&
              (pathname === route.path ||
                pathname.startsWith(`${route.path}/`));

            return (
              <React.Fragment key={route.path}>
                {!isOpen ? (
                  <CustomTooltip content={route.name} side="right">
                    <Link
                      href={route.path || "#"}
                      prefetch={false}
                      onClick={() => {
                        if (window.innerWidth < 768) {
                          onClose();
                        }
                      }}
                      className={cn(
                        `p-1 cursor-pointer ${index === 0 ? "mt-0" : "mt-1"} w-full transition-all duration-200 gap-2 flex items-center pl-3 rounded-r-lg`,
                        isActive
                          ? "font-bold shadow-sm bg-primary text-white"
                          : "hover:text-primary",
                      )}
                    >
                      <route.Icon
                        size={fullScreen ? 10 : 24}
                        className="shrink-0 mx-2"
                      />
                    </Link>
                  </CustomTooltip>
                ) : (
                  <Link
                    href={route.path || "#"}
                    prefetch={false}
                    onClick={() => {
                      if (window.innerWidth < 768) {
                        onClose();
                      }
                    }}
                    className={cn(
                      `p-2 cursor-pointer ${index === 0 ? "mt-0" : "mt-1"} w-full transition-all duration-200 gap-3 flex items-center !rounded-r-lg`,
                      "justify-start",
                      isActive
                        ? "font-bold shadow-sm bg-primary text-white"
                        : "hover:text-primary",
                    )}
                  >
                    <route.Icon
                      size={fullScreen ? 15 : 24}
                      className={cn("shrink-0", fullScreen && "!text-primary")}
                    />
                    <span
                      className={cn(
                        "truncate animate-in fade-in slide-in-from-left-2 duration-300",
                        fullScreen && "!text-primary",
                      )}
                    >
                      {route.name}
                    </span>
                  </Link>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
